// Theme augmentation - must be imported before any MUI usage
import "./theme-augmentation";

import { BrandclubStoreConfigUtils, FingerprintJSProvider, splitSDKConfig } from "@brandclub/common-ui";
import { SplitFactoryProvider } from "@splitsoftware/splitio-react";
import { QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { queryClient } from "./api/cache/queryClient";
import { AppInitializer } from "./AppInitializer";
import CheckoutShopApp from "./CheckoutShopApp";
import "./main.css";
import "./main.generated.css";
import { BCRoutingContextProvider } from "./providers/BCRoutingContextProvider";
import { LocationTrackingContextProvider } from "./providers/LocationTrackingContextProvider";
import getStore from "./redux/store";
import { HOST_DOMAIN } from "./utils/domain";
import { setCheckoutAnalyticsSuppressed } from "./analytics/policy";
import { adoptHostMixpanel } from "./analytics/hostMixpanel";

export {
  openStoreLocator,
  askConcierge,
  openConcierge,
  addToBag,
  openBag,
  openCheckout,
  getCartCount,
  onCartCountChange,
  getAuthState,
  onAuthStateChange,
  getHostDomain,
  adoptHostMixpanel,
  getAuthorizationHeader,
  redeemAndApplyLoginCode,
  signOut,
} from "./public-api";

/**
 * How THIS copy of the bundle was started.
 *
 * A single page can carry TWO copies: a legacy `<script>` a brand pasted into
 * their Shopify theme years ago (`injectCheckoutBar.ts`), and our Shopify app
 * embed. `renderBarScript` is first-caller-wins, so only one of them actually
 * mounts — but every decision below must still be answerable PER COPY, never
 * per store.
 *
 * That distinction is not academic: the concierge visibility check used to ask
 * "is this STORE on the App Proxy?", a store-wide fact. Flipping that setting
 * for PetArmor therefore un-gated the concierge on the LEGACY copy too, and it
 * went to 100% of shoppers past their Split rollout (live, 2026-09-01).
 */
export type BcMountSource = 'theme-script' | 'app-proxy' | 'pages-storefront';

/** Options a host passes when mounting the checkout app. */
export interface BcCheckoutMountOptions {
  /** How this copy was started. Omitted ⇒ `theme-script` (a bare
   *  `renderBarScript()` from a merchant's own theme). */
  mountSource?: BcMountSource;
  /**
   * App-Proxy hosts ONLY: whether the concierge's proactive UI (floating
   * button + conversation starters) may show on this page load.
   *
   * The App-Proxy loader owns this decision because only it knows the store's
   * Off/Preview/Live switch and whether THIS browser is a marked previewer.
   * Omitted ⇒ `true`, which is how an older `widget.js` (that only sends
   * `conciergeControlled`) keeps behaving exactly as it does today.
   */
  showConcierge?: boolean;
  /**
   * Whether this page load may mount the BRANDCLUB CART — the bag button, the
   * in-page Add to bag, the profile/membership icons, the bag drawer and the
   * checkout bar.
   *
   * App-Proxy hosts pass the store's CHECKOUT capability here (D-49: on the
   * Shopify surface, "in-app checkout on" means the Brandclub cart, not
   * Shopify's native one). It is a SEPARATE switch from `showConcierge` on
   * purpose: one bundle carries both features, and a brand cutting over needs
   * to be able to run either without the other.
   *
   * Omitted ⇒ `true` — today's behaviour for every other host (legacy theme
   * scripts, the Pages storefront) and for an older `widget.js` that does not
   * send it yet.
   */
  showCheckout?: boolean;
  /** @deprecated Superseded by `mountSource`. Still honoured so an older
   *  `widget.js` — served by an api-rewards that has not deployed yet — keeps
   *  working: it means "keep the pane mounted", nothing more. */
  conciergeControlled?: boolean;
  /** Opt-in: the HOST owns analytics itself, so this app must NOT emit its own
   *  Mixpanel events (page-view AND commerce/action events). The canonical Pages
   *  storefront always sets it — it re-emits those events with the canonical
   *  `webApp:organic:*` shapes so Mixpanel keeps the same metrics; if the app
   *  also fired them, every event would double-count. Gates only events routed
   *  through `useTracking`; concierge tracking is a separate path and is kept.
   *  Omitted on every other host → the app emits exactly as before. */
  suppressAnalytics?: boolean;
  /** The host's own already-initialized Mixpanel SDK instance
   *  (`AnalyticsService.getMixpanelInstance()`). Given one, this app publishes the
   *  events that survive `suppressAnalytics` — i.e. concierge — through the HOST's
   *  instance instead of standing up its own. This bundle ships its own copies of
   *  `mixpanel-browser` and `AnalyticsService`, so without it the page runs two SDK
   *  instances: two `api_host`s, two batchers, two autocapture page-views per load,
   *  and only the host's instance carries the events-ingest → Doris mirror. Omitted
   *  on every other host → this app initializes its own, exactly as before. */
  mixpanel?: unknown;
  /** The V2 app id of the page this copy mounts on. The Pages storefront passes the id the
   *  host serves the page for (`x-bc-app-id`): there the page sits under a merchant's domain
   *  or the Shopify App Proxy, and the hostname is a different app or none at all. Omitted on
   *  every other host, and on an older bundle → the legacy rule: the app id is the hostname. */
  appId?: string;
}

/**
 * The mount source, tolerating an older host that only knows the deprecated
 * `conciergeControlled` flag.
 *
 * A legacy `conciergeControlled: true` maps to `pages-storefront`, NOT
 * `app-proxy`: both hosts set it, and of the two only `pages-storefront`'s
 * behaviour ("stay mounted, but still gate proactive UI on the Split") is
 * correct to assume when we cannot tell them apart. Guessing `app-proxy` would
 * re-create the very bug this type exists to prevent.
 */
function resolveMountSource(opts: BcCheckoutMountOptions): BcMountSource {
  if (opts.mountSource) return opts.mountSource;
  return opts.conciergeControlled ? 'pages-storefront' : 'theme-script';
}

/** Applied before React renders, so it lands ahead of `AppInitializer`'s init effect and any `track` call. */
function applyHostOptions(opts: BcCheckoutMountOptions) {
  BrandclubStoreConfigUtils.setWebAppId(opts.appId);
  setCheckoutAnalyticsSuppressed(!!opts.suppressAnalytics);
  // The instance crosses a bundle boundary, so it is the host bundle's
  // `mixpanel-browser` object — structurally the same SDK, nominally unrelated.
  // A host that mounts before it has one hands it over later, through
  // `adoptHostMixpanel` on the public API.
  adoptHostMixpanel(opts.mixpanel);
}

export default function render(opts: BcCheckoutMountOptions = {}) {
  applyHostOptions(opts);
  const root = ReactDOM.createRoot(document.body);
  root.render(
    <React.StrictMode>
      <BCRoutingContextProvider>
        <QueryClientProvider client={queryClient}>
          <Provider store={getStore()}>
            <LocationTrackingContextProvider>
              <AppInitializer reloadOnSignOut={opts.mountSource !== 'pages-storefront'}>
                <CheckoutShopApp
                  mountSource={resolveMountSource(opts)}
                  showConcierge={opts.showConcierge}
                  showCheckout={opts.showCheckout}
                />
              </AppInitializer>
            </LocationTrackingContextProvider>
          </Provider>
        </QueryClientProvider>
      </BCRoutingContextProvider>
    </React.StrictMode>
  );
}
// Idempotent: if the bar is already mounted (e.g., host script re-invokes on
// SPA route change, userscript polls more than once, defensive double-call),
// bail out instead of appending a second React tree. This previously caused
// duplicate portaled nodes (e.g., two "Where To Buy" buttons) in the host page.
export function renderBarScript(opts: BcCheckoutMountOptions = {}) {
  if (!document.body) return;
  if (document.getElementById("BCCheckoutBar")) return;

  applyHostOptions(opts);

  const container = document.createElement("div");
  container.id = "BCCheckoutBar";
  container.classList.add("BCCheckoutBar");
  document.body.appendChild(container);

  ReactDOM.createRoot(container).render(
    <React.StrictMode>
      <BCRoutingContextProvider>
        <QueryClientProvider client={queryClient}>
          <Provider store={getStore()}>
            <SplitFactoryProvider config={splitSDKConfig}>
              <FingerprintJSProvider hostDomainName={HOST_DOMAIN}>
                <LocationTrackingContextProvider>
                  <AppInitializer reloadOnSignOut={opts.mountSource !== 'pages-storefront'}>
                    <CheckoutShopApp
                  mountSource={resolveMountSource(opts)}
                  showConcierge={opts.showConcierge}
                  showCheckout={opts.showCheckout}
                />
                  </AppInitializer>
                </LocationTrackingContextProvider>
              </FingerprintJSProvider>
            </SplitFactoryProvider>
          </Provider>
        </QueryClientProvider>
      </BCRoutingContextProvider>
    </React.StrictMode>
  );
}
